<template>
<div>
    <div class="calcDescription peClassification" id="peClassification-header">
        <h2>{{getLocale(content.labels.tool_title)}}</h2>
    </div>
    
    <div class="peClassification calcItem groupItem" :class="{standalone: options.standalone}">
        <div id="peClassification">

            <v-form ref="peClassification" v-model="validForm" @submit.prevent="handleSubmit" @keydown.enter.prevent>
                <!-- ============================================================
                     SCREEN 1 — ENTRY / DISCLAIMER
                     ============================================================ -->
                <div v-if="screen === 'entry'" class="pe-entry">

                    <div v-if="options.showLanguageToggle" class="pe-lang-toggle" role="group" :aria-label="getLocale(content.labels.language_toggle_label)">
                        <button
                            type="button"
                            class="pe-lang-pill"
                            :class="{active: activeLanguage === 'en-ca'}"
                            @click="setLanguage('en-ca')">English</button>
                        <button
                            type="button"
                            class="pe-lang-pill"
                            :class="{active: activeLanguage === 'fr-ca'}"
                            @click="setLanguage('fr-ca')">Français</button>
                    </div>

                    <div class="pe-guideline-badge">{{getLocale(content.labels.guideline_tag)}}</div>

                    <p class="pe-intro-text">{{getLocale(content.labels.intro_body)}}</p>

                    <label class="pe-disclaimer">
                        <input type="checkbox" v-model="disclaimerAccepted" />
                        <span>{{getLocale(content.labels.disclaimer_text)}}</span>
                    </label>

                    <button
                        type="button"
                        class="pe-btn pe-btn-primary pe-btn-launch"
                        :disabled="!disclaimerAccepted"
                        @click="launch">
                        {{getLocale(content.labels.launch_button)}}
                    </button>

                    <p class="pe-ack-text">{{getLocale(content.labels.pfizer_acknowledgement)}}</p>
                </div>

                <!-- ============================================================
                     QUESTION FLOW
                     ============================================================ -->
                <div v-else-if="screen === 'question' && currentQuestion" class="pe-question-flow">

                    <div class="pe-progress-wrap">
                        <div class="pe-progress-label">
                            {{getLocale(content.labels.step_label)}} {{history.length + 1}} {{getLocale(content.labels.step_of)}} {{approxTotalSteps}}
                        </div>
                        <div class="pe-progress-track">
                            <div class="pe-progress-fill" :style="{width: progressPercent + '%'}"></div>
                        </div>
                    </div>

                    <div class="question pe-question-card">
                        <div class="pe-question-text">{{getLocale(currentQuestion.text)}}</div>

                        <div v-if="currentQuestion.help" class="pe-help">
                            <button type="button" class="pe-help-toggle" @click="helpOpen = !helpOpen" :aria-expanded="helpOpen">
                                <span aria-hidden="true">ⓘ</span> {{getLocale(content.labels.help_toggle)}}
                            </button>
                            <div v-if="helpOpen" class="pe-help-body">{{getLocale(currentQuestion.help)}}</div>
                        </div>

                        <!-- Yes / No (with optional inline sub-classification reveal) -->
                        <template v-if="currentQuestion.type === 'yesno' || currentQuestion.type === 'yesno_sub'">
                            <div class="pe-yesno-row">
                                <button
                                    type="button"
                                    class="pe-btn pe-btn-yesno pe-btn-yes"
                                    :class="{selected: form[currentQuestion.id] === 'yes'}"
                                    @click="answerYesNo(currentQuestion, 'yes')">
                                    {{getLocale(content.labels.yes)}}
                                </button>
                                <button
                                    type="button"
                                    class="pe-btn pe-btn-yesno pe-btn-no"
                                    :class="{selected: form[currentQuestion.id] === 'no'}"
                                    @click="answerYesNo(currentQuestion, 'no')">
                                    {{getLocale(content.labels.no)}}
                                </button>
                            </div>

                            <!-- inline sub-classification (e.g. E1/E2, D1/D2) revealed after Yes -->
                            <div v-if="currentQuestion.type === 'yesno_sub' && form[currentQuestion.id] === 'yes'" class="pe-sub-options">
                                <div class="question-subtitle">{{getLocale(currentQuestion.subPrompt)}}</div>
                                <label v-for="opt in currentQuestion.subOptions" :key="opt.value" class="pe-radio-option">
                                    <input
                                        type="radio"
                                        :name="currentQuestion.id + '_sub'"
                                        :value="opt.value"
                                        v-model="form[currentQuestion.subFieldId]" />
                                    <span>{{getLocale(opt.label)}}</span>
                                </label>
                            </div>
                        </template>

                        <!-- Single-select radio (yes/no/unknown style, RV status, biomarkers, etc.) -->
                        <template v-else-if="currentQuestion.type === 'radio'">
                            <div class="pe-radio-list">
                                <label v-for="opt in currentQuestion.options" :key="opt.value" class="pe-radio-option question-input">
                                    <input
                                        type="radio"
                                        :name="currentQuestion.id"
                                        :value="opt.value"
                                        v-model="form[currentQuestion.id]" />
                                    <span>{{getLocale(opt.label)}}</span>
                                </label>
                            </div>
                        </template>

                        <!-- Severity score: tool selector + numeric intake -->
                        <template v-else-if="currentQuestion.type === 'severity_score'">
                            <div class="pe-radio-list">
                                <label v-for="opt in currentQuestion.options" :key="opt.value" class="pe-radio-option question-input">
                                    <input
                                        type="radio"
                                        name="q5_score_tool"
                                        :value="opt.value"
                                        v-model="form.q5_score_tool" />
                                    <span>
                                        {{getLocale(opt.label)}}
                                        <sup v-if="opt.infoLink">
                                            <a :href="opt.infoLink" target="_blank" class="pe-info-link" :aria-label="getLocale(content.labels.infoLink)">
                                                <v-icon size="medium" color="primary">mdi-calculator</v-icon>
                                            </a>
                                        </sup>
                                    </span>
                                    <span v-if="form.q5_score_tool === opt.value" class="pe-inline-input">
                                        <input
                                            type="number"
                                            class="question-input pe-number-input"
                                            v-model.number="form.q5_score_value"
                                            :min="opt.min"
                                            :max="opt.max"
                                            :step="opt.step"
                                            :placeholder="getLocale(opt.placeholder)" />
                                    </span>
                                </label>
                            </div>
                            <div v-if="scoreValidationMessage" class="pe-error">{{getLocale(scoreValidationMessage)}}</div>
                        </template>

                        <div v-if="currentQuestion.notAvailableNote" class="pe-note">{{getLocale(currentQuestion.notAvailableNote)}}</div>
                    </div>

                    <p class="pe-citation">{{getLocale(content.labels.guideline_citation)}}</p>

                    <div class="pe-nav-row pe-nav-row-split">
                        <button v-if="history.length>0" type="button" class="pe-btn pe-btn-back" @click="goBack()">
                            ← {{getLocale(content.labels.back_button)}}
                        </button>
                        <button
                            type="button"
                            class="pe-btn pe-btn-primary pe-btn-continue"
                            :disabled="!canContinue"
                            @click="goNext()">
                            {{getLocale(content.labels.continue_button)}} →
                        </button>
                    </div>
                </div>

                <!-- ============================================================
                     SCREEN 5 — RESULT
                     ============================================================ -->
                <div v-else-if="screen === 'result' && classificationResult" class="pe-result-screen">

                    <div class="pe-result-card" :class="'pe-cat-' + classificationResult.group">
                        <div class="pe-result-top">
                            <div class="pe-result-badge">{{classificationResult.code}}</div>
                            <div class="pe-result-guideline">{{getLocale(content.labels.guideline_short)}}</div>
                        </div>
                        <div class="pe-result-name">{{getLocale(classificationResult.content.name)}}</div>
                        <div v-if="classificationResult.hasRespiratoryModifier" class="pe-result-name">
                            {{ getLocale(content.respiratoryModifierText[classificationResult.group]) }}
                        </div>

                        <div class="pe-result-desc">{{getLocale(classificationResult.content.description)}}</div>
                        <div v-if="classificationResult.incompleteWorkup" class="pe-result-flag">
                            {{getLocale(content.labels.incomplete_workup_flag)}}
                        </div>
                        <div v-if="classificationResult.unconfirmedClot" class="pe-result-flag">
                            {{getLocale(content.labels.unconfirmed_clot_flag)}}
                        </div>
                    </div>

                    <details class="pe-inputs-summary">
                        <summary>{{getLocale(content.labels.inputs_used)}}</summary>
                        <ul>
                            <li v-for="row in answeredSummary" :key="row.id">
                                <strong>{{getLocale(row.question)}}</strong> — {{getLocale(row.answer)}}
                            </li>
                        </ul>
                    </details>

                    <p class="pe-citation">{{getLocale(content.labels.guideline_citation)}}</p>

                    <div class="pe-result-actions">
                        <button type="button" class="pe-btn pe-btn-back" @click="goBack()">
                            ← {{getLocale(content.labels.back_button)}}
                        </button>
                        <button type="button" class="pe-btn pe-btn-secondary" @click="startOver()">
                            {{getLocale(content.labels.start_over_button)}}
                        </button>
                        <button type="button" class="pe-btn pe-btn-primary" @click="copyResult()">
                            {{getLocale(content.labels.share_button)}}
                        </button>
                        <button type="button" class="pe-btn pe-btn-primary" @click="download()">
                            {{getLocale(content.labels.download_pdf)}}
                        </button>
                    </div>
                    <div v-if="copyConfirmed" class="pe-copy-confirm">{{getLocale(content.labels.copied_confirmation)}}</div>

                    <p class="pe-ack-text">{{getLocale(content.labels.pfizer_acknowledgement)}}</p>
                </div>

            </v-form>

            <!-- Printable / PDF summary, follows existing app PDF pattern -->
            <vue-html2pdf
                ref="html2Pdf_recommendations"
                :show-layout="false"
                :float-layout="true"
                :enable-download="false"
                :preview-modal="true"
                :paginate-elements-by-height="1400"
                filename="pe-classification-summary"
                pdf-format="letter"
                pdf-orientation="portrait"
                pdf-content-width="100%"
                @beforeDownload="ui.snackbar.display = false">
                <section slot="pdf-content">
                    <div v-if="classificationResult" class="pe-pdf-summary">
                        <h3>{{getLocale(content.labels.pdf_title)}}</h3>
                        <p>{{getLocale(content.labels.pdf_date_label)}}: {{ new Date().toLocaleString(activeLanguage) }}</p>
                        <hr />
                        <h4>{{getLocale(content.labels.classification_heading)}}</h4>
                        <div class="pe-pdf-classification" :class="'pe-cat-' + classificationResult.group">
                            <span class="pe-pdf-classification-code">{{classificationResult.code}}</span>
                            <span class="pe-pdf-classification-name">{{getLocale(classificationResult.content.name)}}</span>
                        </div>
                        <div v-if="classificationResult.incompleteWorkup" class="pe-note">{{getLocale(content.labels.incomplete_workup_flag)}}</div>
                        <div v-if="classificationResult.unconfirmedClot" class="pe-note">{{getLocale(content.labels.unconfirmed_clot_flag)}}</div>

                        <h4>{{getLocale(content.labels.inputs_used)}}</h4>
                        <div v-for="row in answeredSummary" :key="row.id">
                            <strong>{{getLocale(row.question)}}</strong>
                            <div>{{getLocale(row.answer)}}</div>
                        </div>

                        <h4>{{getLocale(content.labels.recommendations)}}</h4>
                        <div>{{getLocale(classificationResult.content.description)}}</div>
                        <p>{{getLocale(content.labels.guideline_citation)}}</p>
                        <p>{{getLocale(content.labels.pfizer_acknowledgement)}}</p>
                    </div>
                </section>
            </vue-html2pdf>

            <div v-if="ui.snackbar.display" class="pe-snackbar">{{ui.snackbar.message}}</div>

        </div>

    </div>
</div>
</template>

<script>
import VueHtml2pdf from 'vue-html2pdf'

/* =============================================================================================
 * CONTENT
 * -----------------------------------------------------------------------------------------------
 * All user-facing copy lives in this single bilingual JSON object (en-ca / fr-ca), separated from
 * the component logic below so that clinical/UX content can be reviewed, translated, and updated
 * without touching the flow, validation, or classification code.
 *
 * NOTE — French copy sourced from the Wireframe & Technical Brief (March 2026) where provided;
 * remaining strings are a working machine-assisted draft and MUST be reviewed & signed off by a
 * bilingual clinician before release (see Brief §5, §5.1, Appendix A).
 * ============================================================================================= */
const CONTENT = {
    labels: {
        tool_title: {"en-ca": "PE Severity Classification", "fr-ca": "Classification de la gravité de l'EP"},
        guideline_tag: {"en-ca": "2026 AHA/ACC Acute PE Guideline", "fr-ca": "Ligne directrice AHA/ACC 2026 sur l'EP aiguë"},
        guideline_short: {"en-ca": "AHA/ACC 2026", "fr-ca": "AHA/ACC 2026"},
        language_toggle_label: {"en-ca": "Language", "fr-ca": "Langue"},
        intro_body: {
            "en-ca": "This tool assigns an AHA/ACC severity category (A–E) to a patient with confirmed or suspected acute PE based on clinical findings.",
            "fr-ca": "Cet outil attribue une catégorie de gravité AHA/ACC (A–E) à un patient atteint d'une EP aiguë confirmée ou suspectée."
        },
        disclaimer_text: {
            "en-ca": "I understand this tool is a clinical decision support aid and does not replace clinical judgment.",
            "fr-ca": "Je comprends que cet outil est une aide à la décision clinique et ne remplace pas le jugement clinique."
        },
        launch_button: {"en-ca": "Launch Tool", "fr-ca": "Lancer l'outil"},
        back_button: {"en-ca": "Back", "fr-ca": "Retour"},
        confirm_button: {"en-ca": "Confirm", "fr-ca": "Confirmer"},
        continue_button: {"en-ca": "Continue", "fr-ca": "Continuer"},
        yes: {"en-ca": "Yes", "fr-ca": "Oui"},
        no: {"en-ca": "No", "fr-ca": "Non"},
        help_toggle: {"en-ca": "Tap to expand", "fr-ca": "Appuyer pour développer"},
        step_label: {"en-ca": "Step", "fr-ca": "Étape"},
        step_of: {"en-ca": "of ~", "fr-ca": "sur ~"},
        inputs_used: {"en-ca": "Inputs used", "fr-ca": "Réponses saisies"},
        start_over_button: {"en-ca": "Start Over", "fr-ca": "Recommencer"},
        share_button: {"en-ca": "Copy Result", "fr-ca": "Copier le résultat"},
        download_pdf: {"en-ca": "Download Summary", "fr-ca": "Télécharger le PDF"},
        generate_pdf: {"en-ca": "Generating PDF...", "fr-ca": "Génération du PDF..."},
        copied_confirmation: {"en-ca": "Result copied to clipboard.", "fr-ca": "Résultat copié dans le presse-papiers."},
        recommendations: {"en-ca": "Recommendations", "fr-ca": "Recommandations"},
        classification_heading: {"en-ca": "Classification", "fr-ca": "Classification"},
        pdf_title: {"en-ca": "PE SEVERITY CLASSIFICATION SUMMARY", "fr-ca": "RÉSUMÉ DE LA CLASSIFICATION DE GRAVITÉ DE L'EP"},
        pdf_date_label: {"en-ca": "Date", "fr-ca": "Date"},
        guideline_citation: {
            "en-ca": "Source: 2026 AHA/ACC Acute PE Guideline — Figure 2. Creager et al., JACC 2026.",
            "fr-ca": "Source : Ligne directrice AHA/ACC 2026 sur l'EP aiguë — Figure 2. Creager et al., JACC 2026."
        },
        infoLink: {"en-ca": "More info", "fr-ca": "Plus d'informations"},
        pfizer_acknowledgement: {
            "en-ca": "Supported by an unrestricted educational grant from Pfizer Canada.",
            "fr-ca": "Soutenu par une subvention éducative non restrictive de Pfizer Canada."
        },
        incomplete_workup_flag: {
            "en-ca": "RV status and biomarkers were both unavailable — this category is provisional pending further workup.",
            "fr-ca": "L'état du VD et les biomarqueurs étaient tous deux non disponibles — cette catégorie est provisoire en attendant une évaluation complémentaire."
        },
        unconfirmed_clot_flag: {
            "en-ca": "Clot location (subsegmental vs. non-subsegmental) was not confirmed on imaging.",
            "fr-ca": "La localisation du caillot (sous-segmentaire ou non) n'a pas été confirmée à l'imagerie."
        },
        not_available_hint: {
            "en-ca": "Normotensive shock criteria require lab and hemodynamic data. Select \"Not available\" if these are not on hand.",
            "fr-ca": "Les critères de choc normotensif nécessitent des données de laboratoire et hémodynamiques. Sélectionnez « Non disponible » si elles ne sont pas accessibles."
        }
    },

    /* ---------------------------------------------------------------------------------------
     * QUESTIONS — one entry per node in the branching decision tree. `id` values are referenced
     * by QUESTION_FLOW (below, in the logic section) to decide what comes next, and by
     * classifyPE() to read answers back out of the form.
     * ------------------------------------------------------------------------------------- */
    questions: [
        {
            id: 'q1_shock',
            type: 'yesno_sub',
            text: {
                "en-ca": "Is the patient in cardiogenic shock, refractory cardiogenic shock, or cardiac arrest?",
                "fr-ca": "Le patient est-il en état de choc cardiogénique, de choc cardiogénique réfractaire ou d'arrêt cardiaque?"
            },
            help: {
                "en-ca": "Cardiogenic shock: SBP <90 mmHg or ↓ >40 mmHg with end-organ dysfunction. Refractory shock: not responding to IV fluids or vasopressors.",
                "fr-ca": "Choc cardiogénique : PAS <90 mmHg ou ↓ >40 mmHg avec dysfonction des organes cibles. Choc réfractaire : ne répondant pas aux liquides IV ou aux vasopresseurs."
            },
            subFieldId: 'q1a_e_subtype',
            subPrompt: {"en-ca": "Select the subtype:", "fr-ca": "Sélectionnez le sous-type :"},
            subOptions: [
                {value: 'e1', label: {"en-ca": "E1 — Cardiogenic shock (recurrent/persistent hypotension)", "fr-ca": "E1 — Choc cardiogénique (hypotension récurrente ou persistante)"}},
                {value: 'e2', label: {"en-ca": "E2 — Refractory cardiogenic shock or cardiac arrest", "fr-ca": "E2 — Choc cardiogénique réfractaire ou arrêt cardiaque"}}
            ]
        },
        {
            id: 'q2_hypotension',
            type: 'yesno_sub',
            text: {
                "en-ca": "Is there evidence of transient hypotension, or signs of normotensive shock (without meeting criteria for cardiogenic shock)?",
                "fr-ca": "Y a-t-il des signes d'hypotension transitoire ou de choc normotensif (sans répondre aux critères de choc cardiogénique)?"
            },
            help: {
                "en-ca": "Transient hypotension: SBP <90 mmHg or ↓ >40 mmHg lasting <15 min, or responding to IV fluids. Normotensive shock: lactate >2 mmol/L, AKI, urine output <0.5 mL/kg/hr, mental status change, CI <2.2 L/min/m², MAP <60 mmHg, elevated SCAI stage or CPES score.",
                "fr-ca": "Hypotension transitoire : PAS <90 mmHg ↓ >40 mmHg durant <15 min, ou répondant aux liquides IV. Choc normotensif : lactate >2 mmol/L, IRA, débit urinaire <0,5 mL/kg/h, altération de l'état mental, IC <2,2 L/min/m², PAM <60 mmHg, stade SCAI ou score CPES élevé."
            },
            subFieldId: 'q2a_d_subtype',
            subPrompt: {"en-ca": "Select the subtype:", "fr-ca": "Sélectionnez le sous-type :"},
            subOptions: [
                {value: 'd1', label: {"en-ca": "D1 — Transient hypotension: SBP <90 or ↓>40 mmHg lasting <15 min or responding to IV fluids", "fr-ca": "D1 — Hypotension transitoire"}},
                {value: 'd2', label: {"en-ca": "D2 — Normotensive shock: lactate >2 mmol/L, AKI, urine output <0.5 mL/kg/hr, mental status change, CI <2.2 L/min/m², MAP <60 mmHg, elevated SCAI stage or CPES score", "fr-ca": "D2 — Choc normotensif"}}
            ],
            // notAvailableNote: {
            //     "en-ca": "Normotensive shock criteria require lab and hemodynamic data.",
            //     "fr-ca": "Les critères de choc normotensif nécessitent des données de laboratoire et hémodynamiques."
            // }
        },
        {
            id: 'q3_symptoms',
            type: 'yesno',
            text: {
                "en-ca": "Does the patient have symptoms attributable to the PE? (i.e., this is NOT an incidental finding)",
                "fr-ca": "Le patient présente-t-il des symptômes attribuables à l'EP? (c'est-à-dire qu'il ne s'agit PAS d'une découverte fortuite)"
            }
        },
        {
            id: 'q4_subsegmental',
            type: 'radio',
            text: {
                "en-ca": "Based on imaging, is the PE subsegmental?",
                "fr-ca": "D'après l'imagerie, l'EP est-elle sous-segmentaire?"
            },
            options: [
                {value: 'yes', label: {"en-ca": "Yes — subsegmental", "fr-ca": "Oui — sous-segmentaire"}},
                {value: 'no', label: {"en-ca": "No — non-subsegmental", "fr-ca": "Non — non sous-segmentaire"}},
                {value: 'unknown', label: {"en-ca": "Unknown", "fr-ca": "Inconnu"}}
            ],
            // notAvailableNote: {
            //     "en-ca": "The B1/B2 distinction informs triage (home vs. hospital) and anticoagulation decisions — this will be noted on the result screen.",
            //     "fr-ca": "La distinction B1/B2 oriente le triage (domicile ou hôpital) et les décisions d'anticoagulation — cela sera indiqué sur l'écran de résultat."
            // }
        },
        {
            id: 'q5_severity',
            type: 'severity_score',
            text: {
                "en-ca": "Select the clinical severity scoring tool used for this patient:",
                "fr-ca": "Sélectionnez l'outil de score de gravité clinique utilisé :"
            },
            // help: {
            //     "en-ca": "Out of scope for this tool: PESI, sPESI, and Bova scores are entered here as values already calculated elsewhere (e.g. a separate TC calculator) — this tool does not compute them.",
            //     "fr-ca": "Hors de la portée de cet outil : les scores PESI, sPESI et Bova sont saisis ici comme des valeurs déjà calculées ailleurs (p. ex. un autre calculateur de TC) — cet outil ne les calcule pas."
            // },
            options: [
                {
                    value: 'pesi', label: {"en-ca": "PESI Score", "fr-ca": "Score PESI"},
                    min: 0, max: 300, step: 1, placeholder: {"en-ca": "Enter PESI value", "fr-ca": "Entrer la valeur du PESI"},
                    infoLink: "https://thrombosiscanada.ca/hcp/practice/clinical_tools?calc=pesi"
                },
                {
                    value: 'spesi', label: {"en-ca": "sPESI Score (0 or ≥1)", "fr-ca": "Score sPESI (0 ou ≥1)"},
                    min: 0, max: 10, step: 1, placeholder: {"en-ca": "Enter sPESI value", "fr-ca": "Entrer la valeur du sPESI"},
                    infoLink: "https://thrombosiscanada.ca/hcp/practice/clinical_tools?calc=simplifiedPesi"
                },
                {
                    value: 'bova', label: {"en-ca": "Bova Score", "fr-ca": "Score Bova"},
                    min: 0, max: 15, step: 1, placeholder: {"en-ca": "Enter Bova value", "fr-ca": "Entrer la valeur du score Bova"}
                }
            ]
        },
        {
            id: 'q6_rv',
            type: 'radio',
            text: {
                "en-ca": "What is the right ventricular (RV) status on echocardiogram or CT?",
                "fr-ca": "Quel est l'état du ventricule droit (VD) à l'échocardiogramme ou au scanner?"
            },
            options: [
                {value: 'normal', label: {"en-ca": "Normal RV size and function", "fr-ca": "Taille et fonction du VD normales"}},
                {value: 'abnormal', label: {"en-ca": "Abnormal RV (enlarged or reduced function)", "fr-ca": "VD anormal (dilaté ou fonction réduite)"}},
                {value: 'not_assessed', label: {"en-ca": "Not assessed", "fr-ca": "Non évalué"}}
            ]
        },
        {
            id: 'q7_biomarkers',
            type: 'radio',
            text: {
                "en-ca": "Cardiac biomarker status (troponin I/T and/or BNP/NT-proBNP):",
                "fr-ca": "Statut des biomarqueurs cardiaques (troponine I/T et/ou BNP/NT-proBNP) :"
            },
            options: [
                {value: 'normal', label: {"en-ca": "All biomarkers normal", "fr-ca": "Tous les biomarqueurs normaux"}},
                {value: 'abnormal', label: {"en-ca": "≥1 abnormal biomarker", "fr-ca": "≥1 biomarqueur anormal"}},
                {value: 'not_available', label: {"en-ca": "Not available", "fr-ca": "Non disponible"}}
            ]
        },
        {
            id: 'q8_resp',
            type: 'yesno',
            // text depends on the category group reached so far (bc | d | e) — see
            // RESPIRATORY_CRITERIA_TEXT in the logic section and currentQuestion() computed.
            text: null
        }
    ],

    /* Respiratory modifier (R) question text, keyed by category group. Category A has no entry
     * on purpose — per the Brief, the 2026 guideline figure does not depict an R modifier for
     * Category A (see Appendix B, Q1 — open item for clinical lead). */
    respiratoryCriteriaText: {
        bc: {
            "en-ca": "Does the patient have: O₂ saturation <90%, OR respiratory rate ≥30, OR a requirement for supplemental oxygen?",
            "fr-ca": "Le patient présente-t-il : SpO₂ <90 %, OU FR ≥30, OU besoin d'oxygène d'appoint?"
        },
        d: {
            "en-ca": "Does the patient require >6 L O₂ via nasal cannula, OR is a non-rebreather (NRB) mask in use?",
            "fr-ca": "Le patient nécessite-t-il >6 L O₂ par lunette nasale, OU un masque à haute concentration (MHC)"                      
        },
        e: {
            "en-ca": "Is the patient in hypoxemic respiratory failure, OR requiring ventilatory support (invasive or non-invasive)?",
            "fr-ca": "Le patient est-il en insuffisance respiratoire hypoxémique, OU nécessite-t-il une ventilation (invasive ou non)?"
        }
    },

    /* ---------------------------------------------------------------------------------------
     * Respiratory Modifier text — plain-language copy to explain the R modifier on the result screen, drawn from Appendix A.
     * ------------------------------------------------------------------------------------- */
    respiratoryModifierText: {
        b: {
            "en-ca": "Respiratory Modifier  — O₂ saturation <90%, OR respiratory rate ≥30, OR need for supplemental oxygen",
            "fr-ca": "Respiratory Modifier  — O₂ saturation <90%, OR respiratory rate ≥30, OR need for supplemental oxygen"
        },
        c: {
            "en-ca": "Respiratory Modifier  — O₂ saturation <90%, OR respiratory rate ≥30, OR need for supplemental oxygen",
            "fr-ca": "Respiratory Modifier  — O₂ saturation <90%, OR respiratory rate ≥30, OR need for supplemental oxygen"
        },
        d: {
            "en-ca": "Respiratory Modifier  — Requires >6 L via nasal cannula, OR use of a non-rebreather (NRB) mask",
            "fr-ca": "Respiratory Modifier  — Requires >6 L via nasal cannula, OR use of a non-rebreather (NRB) mask"
        },
        e: {
            "en-ca": "Respiratory Modifier  — Hypoxemic respiratory failure, OR ventilatory failure (invasive or non-invasive)",
            "fr-ca": "Respiratory Modifier  — Hypoxemic respiratory failure, OR ventilatory failure (invasive or non-invasive)"
        },
    }
     ,

    /* ---------------------------------------------------------------------------------------
     * RESULTS — plain-language copy per final category/subcategory code, drawn from Appendix A.
     * Draft copy — pending Thrombosis Canada clinical lead sign-off per the Brief.
     * ------------------------------------------------------------------------------------- */
    results: {
        A: {
            name: {"en-ca": "Subclinical PE", "fr-ca": "EP subclinique"},
            description: {
                "en-ca": "This patient has an incidentally discovered, asymptomatic PE. Management should be individualized. Anticoagulation decisions may differ from symptomatic PE.",
                "fr-ca": "Cette EP a été découverte fortuitement chez un patient asymptomatique. La prise en charge doit être individualisée. Les décisions d'anticoagulation peuvent différer de celles d'une EP symptomatique."
            }
        },
        B1: {
            name: {"en-ca": "Symptomatic — Low Severity (Subsegmental)", "fr-ca": "Symptomatique — Gravité faible (sous-segmentaire)"},
            description: {
                "en-ca": "Subsegmental PE with low clinical severity. The decision to treat with anticoagulation and the need for hospital admission should be individualized, considering clot burden and patient-specific risk.",
                "fr-ca": "EP sous-segmentaire de faible gravité. La décision de traiter par anticoagulation et la nécessité d'une hospitalisation doivent être individualisées."
            }
        },
        B2: {
            name: {"en-ca": "Symptomatic — Low Severity (Non-Subsegmental)", "fr-ca": "Symptomatique — Gravité faible (non sous-segmentaire)"},
            description: {
                "en-ca": "Symptomatic PE with low clinical severity score. Early discharge and outpatient treatment with a DOAC may be appropriate in eligible patients.",
                "fr-ca": "EP symptomatique de faible gravité. Le congé précoce et le traitement ambulatoire par un anticoagulant oral direct peuvent être appropriés chez les patients éligibles."
            }
        },
        C1: {
            name: {"en-ca": "Symptomatic — Elevated Severity (Normal RV, Normal Biomarkers)", "fr-ca": "Symptomatique — Gravité élevée (VD normal, biomarqueurs normaux)"},
            description: {
                "en-ca": "Elevated severity score but preserved RV function and normal biomarkers. Hospital admission is recommended. Monitor closely for deterioration.",
                "fr-ca": "Score de gravité élevé, mais fonction du VD préservée et biomarqueurs normaux. Une hospitalisation est recommandée."
            }
        },
        C2: {
            name: {"en-ca": "Symptomatic — Elevated Severity (Abnormal RV or Biomarkers)", "fr-ca": "Symptomatique — Gravité élevée (VD ou biomarqueurs anormaux)"},
            description: {
                "en-ca": "Elevated severity with either RV dysfunction or abnormal biomarkers. Intermediate-high risk. Close monitoring and multidisciplinary input are warranted.",
                "fr-ca": "Gravité élevée avec dysfonction du VD ou biomarqueurs anormaux. Surveillance rapprochée et évaluation multidisciplinaire recommandées"
            }
        },
        C3: {
            name: {"en-ca": "Symptomatic — Elevated Severity (Abnormal RV and Biomarkers)", "fr-ca": "Symptomatique — Gravité élevée (VD et biomarqueurs anormaux)"},
            description: {
                "en-ca": "Elevated severity with both RV dysfunction and abnormal biomarkers. This patient is at high risk for early deterioration. Consider PERT activation and ICU-level monitoring.",
                "fr-ca": "Gravité élevée avec dysfonction du VD et biomarqueurs anormaux. Risque élevé de détérioration précoce. Considérer l'activation de l'équipe de réponse à l'EP (PERT)."
            }
        },
        Cx: {
            name: {"en-ca": "Symptomatic — Elevated Severity (Unspecified)", "fr-ca": "Symptomatique — Gravité élevée (non précisée)"},
            description: {
                "en-ca": "Elevated severity score, but RV status and biomarkers were both unavailable. Category is provisional — further workup (echo/CT and biomarkers) is recommended to refine subcategory.",
                "fr-ca": "Score de gravité élevé, mais l'état du VD et les biomarqueurs étaient tous deux non disponibles. Catégorie provisoire — une évaluation complémentaire (écho/scanner et biomarqueurs) est recommandée pour préciser la sous-catégorie."
            }
        },
        D1: {
            name: {"en-ca": "Incipient Cardiopulmonary Failure — Transient Hypotension", "fr-ca": "Insuffisance cardiopulmonaire incipiente — Hypotension transitoire"},
            description: {
                "en-ca": "Transient hypotension that self-resolved or responded to IV fluids. High-risk category requiring ICU admission and consideration of advanced reperfusion therapies.",
                "fr-ca": "Hypotension transitoire résolue spontanément ou après remplissage vasculaire. Catégorie à risque élevé nécessitant une admission en soins intensifs."
            }
        },
        D2: {
            name: {"en-ca": "Incipient Cardiopulmonary Failure — Normotensive Shock", "fr-ca": "Insuffisance cardiopulmonaire incipiente — Choc normotensif"},
            description: {
                "en-ca": "Evidence of end-organ hypoperfusion without overt hypotension. Critical category requiring urgent ICU transfer and multidisciplinary evaluation for reperfusion.",
                "fr-ca": "Signes d'hypoperfusion des organes cibles sans hypotension manifeste. Transfert urgent en soins intensifs requis."
            }
        },
        E1: {
            name: {"en-ca": "Cardiopulmonary Failure — Cardiogenic Shock", "fr-ca": "Insuffisance cardiopulmonaire — Choc cardiogénique"},
            description: {
                "en-ca": "Recurrent or persistent hypotension with cardiogenic shock. This is a life-threatening emergency. Systemic thrombolysis or catheter-directed therapy should be urgently evaluated.",
                "fr-ca": "Hypotension persistante avec choc cardiogénique. Urgence vitale. Évaluer en urgence la thrombolyse systémique ou le traitement dirigé par cathéter."
            }
        },
        E2: {
            name: {"en-ca": "Cardiopulmonary Failure — Refractory Shock or Cardiac Arrest", "fr-ca": "Insuffisance cardiopulmonaire — Choc réfractaire ou arrêt cardiaque"},
            description: {
                "en-ca": "Refractory cardiogenic shock or cardiac arrest. Highest acuity category. Immediate escalation of care, including mechanical circulatory support and advanced intervention, is required.",
                "fr-ca": "Choc cardiogénique réfractaire ou arrêt cardiaque. Catégorie de plus haute acuité. Escalade immédiate des soins requise."
            }
        }
    }
}

/* =============================================================================================
 * LOGIC
 * -----------------------------------------------------------------------------------------------
 * Pure, framework-agnostic helper functions — no `this`, no Vue APIs. They take the `form`
 * (plain object of answers) and/or `CONTENT` and return plain data. This keeps the branching
 * logic, classification rules, and respiratory-modifier rules independently testable and
 * separately adaptable (per project requirement) from both the rendering layer and from each
 * other — e.g. `applyRespiratoryModifier` can be swapped or extended without touching
 * `classifyPE`, and vice versa.
 * ============================================================================================= */

/* When RV status AND biomarkers are BOTH unavailable, the Brief (Appendix B, Q3) leaves the
 * default behaviour as an open clinical question: output "C-unspecified" or default to the
 * higher subcategory (C3). This is intentionally a single named constant so it can be flipped
 * in one place once Thrombosis Canada's clinical lead decides. */
const NOT_ASSESSED_C_DEFAULT = 'unspecified' // 'unspecified' | 'C3'

/* Linear graph of "what question comes after this one", expressed as functions of the current
 * form state so that the flow can branch. Returning `null` means "go to the result screen".
 * To add/re-order/remove a question, this is the only place that needs to change (together with
 * the CONTENT.questions entry and, if it affects the outcome, classifyPE()). */
const QUESTION_FLOW = {
    q1_shock: (form) => form.q1_shock === 'yes' ? 'q8_resp' : 'q2_hypotension',
    q2_hypotension: (form) => form.q2_hypotension === 'yes' ? 'q8_resp' : 'q3_symptoms',
    q3_symptoms: (form) => form.q3_symptoms === 'no' ? null : 'q4_subsegmental',
    q4_subsegmental: () => 'q5_severity',
    q5_severity: (form) => isSeverityLow(form) ? null : 'q6_rv',
    q6_rv: () => 'q7_biomarkers',
    q7_biomarkers: () => 'q8_resp',
    q8_resp: () => null
}

function isSeverityLow(form) {
    const tool = form.q5_score_tool
    const value = form.q5_score_value
    if (tool === 'pesi') return value <= 85
    if (tool === 'spesi') return value === 0
    if (tool === 'bova') return value <= 4
    return false
}

/* Which respiratory-modifier criteria set applies, based on the category reached so far.
 * Category A intentionally has no criteria group (see CONTENT.respiratoryCriteriaText note). */
function getRespiratoryCriteriaGroup(form) {
    if (form.q1_shock === 'yes') return 'e'
    if (form.q2_hypotension === 'yes') return 'd'
    if (form.q3_symptoms === 'yes') return 'bc'
    return null
}

/* Determines the base category/subcategory (pre-respiratory-modifier) from the answers gathered
 * so far. Safe to call at any point in the flow — returns whatever can be determined with the
 * answers available and leaves the rest undefined. */
function classifyPE(form) {
    if (form.q1_shock === 'yes') {
        const code = form.q1a_e_subtype === 'e2' ? 'E2' : 'E1'
        return {group: 'e', code, subcategoryKey: code}
    }
    if (form.q2_hypotension === 'yes') {
        const code = form.q2a_d_subtype === 'd2' ? 'D2' : 'D1'
        return {group: 'd', code, subcategoryKey: code}
    }
    if (form.q3_symptoms === 'no') {
        return {group: 'a', code: 'A', subcategoryKey: 'A'}
    }
    if (form.q3_symptoms !== 'yes') {
        return null // not enough information yet
    }

    const unconfirmedClot = form.q4_subsegmental === 'unknown' || !form.q4_subsegmental
    const isSubsegmental = form.q4_subsegmental === 'yes'

    if (!form.q5_score_tool || form.q5_score_value === null || form.q5_score_value === undefined || form.q5_score_value === '') {
        return null
    }

    if (isSeverityLow(form)) {
        const subKey = isSubsegmental ? 'B1' : 'B2'
        return {group: 'b', code: subKey, subcategoryKey: subKey, unconfirmedClot}
    }

    // Elevated severity -> Category C, refined by RV status + biomarkers.
    const rv = form.q6_rv
    const bio = form.q7_biomarkers
    if (!rv || !bio) {
        return {group: 'c', code: 'C', subcategoryKey: null, pending: true}
    }

    const rvAbnormal = rv === 'abnormal'
    const rvUnknown = rv === 'not_assessed'
    const bioAbnormal = bio === 'abnormal'
    const bioUnknown = bio === 'not_available'

    if (rvUnknown && bioUnknown) {
        if (NOT_ASSESSED_C_DEFAULT === 'C3') {
            return {group: 'c', code: 'C3', subcategoryKey: 'C3', incompleteWorkup: true}
        }
        return {group: 'c', code: 'Cx', subcategoryKey: 'Cx', incompleteWorkup: true}
    }
    if (rvAbnormal && bioAbnormal) {
        return {group: 'c', code: 'C3', subcategoryKey: 'C3'}
    }
    if (rvAbnormal || bioAbnormal) {
        return {group: 'c', code: 'C2', subcategoryKey: 'C2'}
    }
    return {group: 'c', code: 'C1', subcategoryKey: 'C1'}
}

/* Appends the "R" respiratory modifier to a base category result when the respiratory question
 * was answered "yes". Category A never carries a modifier. Isolated from classifyPE() so the
 * respiratory-modifier rules can be changed independently (e.g. if Category A gains an R
 * modifier after clinical review — see Appendix B, Q1). */
function applyRespiratoryModifier(baseResult, form) {
    if (!baseResult) return baseResult
    if (baseResult.group === 'a') return baseResult
    if (form.q8_resp !== 'yes') return baseResult
    return Object.assign({}, baseResult, {code: baseResult.code + 'R', hasRespiratoryModifier: true})
}

function validateSeverityScore(question, form) {
    const opt = question.options.find(o => o.value === form.q5_score_tool)
    if (!opt) return {valid: false}
    const value = form.q5_score_value
    if (value === null || value === undefined || value === '' || isNaN(value)) {
        return {valid: false, message: {"en-ca": "Enter a numeric value.", "fr-ca": "Entrez une valeur numérique."}}
    }
    if (value < opt.min || value > opt.max) {
        return {valid: false, message: {"en-ca": "Value is out of the expected range.", "fr-ca": "La valeur est hors de la plage attendue."}}
    }
    return {valid: true}
}

export default {
    name: "peClassification",
    props: {
        language: {
            type: String,
            required: false,
            default: "en-ca"
        },
        options: {
            type: Object,
            required: false,
            default: () => ({
                showLanguageToggle: true,
                autoLaunch: false,
                standalone: true
            })
        }
    },
    components: {
        VueHtml2pdf
    },
    created(){
        this.init()
    },
    data(){
        return {
            content: CONTENT,
            ui: {
                snackbar: {
                    display: false,
                    message: 'Generating PDF',
                    timeout: -1,
                },
                showKT: false
            },
            validForm: false,
            ready: false,
            screen: 'entry', // 'entry' | 'question' | 'result'
            disclaimerAccepted: false,
            activeLanguage: this.language,
            helpOpen: false,
            copyConfirmed: false,
            currentQuestionId: 'q1_shock',
            history: [], // stack of previously-visited question ids, for Back navigation
            form: null,
            rules: {
                answered: [
                    v => !!v || 'Required',
                    v => !!v && v.length > 0 || 'Required',
                ]
            },
            timer: null,
            popup_flow_chart: { img: null },
            pdfDownload: {
                generatingPDF: false,
                pdfLink: {},
            },
        }
    },
    methods: {
        init(){
            this.form = this.getEmptyForm()
            this.history = []
            this.currentQuestionId = 'q1_shock'
            this.screen = 'entry'
            this.disclaimerAccepted = false
            this.copyConfirmed = false

            if(this.options.autoLaunch){
                this.disclaimerAccepted = true
                this.launch()
            }

            
            this.ready = true
            
            let self = this
            setTimeout(() => {
                self.scrollTo('peClassification-header')
            }, 1200)
        },
        handleSubmit(){
            return
        },
        getEmptyForm(){
            return {
                q1_shock: null,
                q1a_e_subtype: null,
                q2_hypotension: null,
                q2a_d_subtype: null,
                q3_symptoms: null,
                q4_subsegmental: null,
                q5_score_tool: null,
                q5_score_value: null,
                q6_rv: null,
                q7_biomarkers: null,
                q8_resp: null
            }
        },
        setLanguage(lang){
            this.activeLanguage = lang
            this.$emit('update:language', lang)
        },
        launch(){
            if (!this.disclaimerAccepted) return
            this.screen = 'question'
            this.currentQuestionId = 'q1_shock'
            this.history = []
            this.helpOpen = false
        },
        answerYesNo(question, value){
            this.form[question.id] = value
            if (question.type === 'yesno_sub') {
                if (value === 'no') {
                    // clear any previously-chosen sub-classification, then advance immediately —
                    // "No" needs no further input on this screen.
                    this.form[question.subFieldId] = null
                    this.goNext()
                }
                // "Yes" still needs a sub-classification choice (E1/E2, D1/D2) before advancing,
                // so wait for the user to pick one and hit Continue.
                return
            }
            // Plain Yes/No questions auto-advance on click, same as before. The Continue button
            // in the nav row below stays enabled once answered, so if the user navigates Back to
            // an already-answered question they can move forward again without re-clicking
            // Yes/No unless they actually want to change it.
            this.goNext()
        },
        goNext(){
            if (!this.canContinue) return
            const nextId = QUESTION_FLOW[this.currentQuestionId]
                ? QUESTION_FLOW[this.currentQuestionId](this.form)
                : null
            this.history.push(this.currentQuestionId)
            this.helpOpen = false
            if (nextId === null) {
                this.screen = 'result'
                this.currentQuestionId = null
            } else {
                this.currentQuestionId = nextId
            }
            this.scrollTo('peClassification-header')
        },
        goBack(){
            if (this.history.length === 0) {
                this.screen = 'entry'
                return
            }
            const prevId = this.history.pop()
            this.currentQuestionId = prevId
            this.screen = 'question'
            this.helpOpen = false
        },
        startOver(){
            this.init()
        },
        copyResult(){
            if (!this.classificationResult) return
            const lines = [
                this.getLocale(this.content.labels.pdf_title),
                this.classificationResult.code + ' — ' + this.getLocale(this.classificationResult.content.name),
                this.getLocale(this.classificationResult.content.description),
                this.getLocale(this.content.labels.guideline_citation)
            ]
            const text = lines.join('\n')
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text)
            }
            this.copyConfirmed = true
            let self = this
            setTimeout(() => { self.copyConfirmed = false }, 3000)
        },
        download(){
            let self = this
            this.ui.snackbar.message = this.getLocale(this.content.labels.generate_pdf)
            this.ui.snackbar.display = true
            clearTimeout(self.timer)
            self.timer = setTimeout(() => {
                self.$refs.html2Pdf_recommendations.generatePdf()
            }, 800);
        },
        scrollTo(targetElemId){
            let el = document.querySelector('#' + targetElemId)
            return el && el.scrollIntoView({behavior: 'smooth'});
        },
        getLocale(source){
            if (source){
                return source[this.activeLanguage] === undefined || !source[this.activeLanguage] ? source["en-ca"] : source[this.activeLanguage]
            }
            return null
        },
    },
    computed: {
        isMobile(){
            return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && !window.MSStream
        },
        currentQuestion(){
            if (!this.currentQuestionId) return null
            const base = this.content.questions.find(q => q.id === this.currentQuestionId)
            if (!base) return null
            if (base.id === 'q8_resp') {
                const group = getRespiratoryCriteriaGroup(this.form)
                return Object.assign({}, base, {
                    text: group ? this.content.respiratoryCriteriaText[group] : {"en-ca": "", "fr-ca": ""}
                })
            }
            return base
        },
        approxTotalSteps(){
            // The Brief specifies the flow is "up to ~7" steps since it terminates early
            // (e.g. Category A resolves after 3 questions, Category E after 1-2).
            return 7
        },
        progressPercent(){
            return Math.min(100, Math.round(((this.history.length + 1) / this.approxTotalSteps) * 100))
        },
        isScoreValid(){
            if (!this.currentQuestion || this.currentQuestion.type !== 'severity_score') return false
            return validateSeverityScore(this.currentQuestion, this.form).valid
        },
        /* Whether the current question has a complete, valid answer — drives the Continue
         * button in the Back/Continue nav row. Kept as one computed per-question-type so a new
         * question type only needs a case added here (and to QUESTION_FLOW / classifyPE if it
         * affects the outcome). */
        canContinue(){
            const q = this.currentQuestion
            if (!q) return false
            const form = this.form
            if (q.type === 'yesno') {
                return form[q.id] === 'yes' || form[q.id] === 'no'
            }
            if (q.type === 'yesno_sub') {
                if (form[q.id] === 'no') return true
                if (form[q.id] === 'yes') return !!form[q.subFieldId]
                return false
            }
            if (q.type === 'radio') {
                return !!form[q.id]
            }
            if (q.type === 'severity_score') {
                return this.isScoreValid
            }
            return false
        },
        scoreValidationMessage(){
            if (!this.currentQuestion || this.currentQuestion.type !== 'severity_score') return null
            if (!this.form.q5_score_tool) return null
            const result = validateSeverityScore(this.currentQuestion, this.form)
            return result.valid ? null : result.message
        },
        classificationResult(){
            let result = classifyPE(this.form)
            if (!result || result.pending) return null
            result = applyRespiratoryModifier(result, this.form)
            const content = this.content.results[result.subcategoryKey] || this.content.results[result.code.replace('R', '')]
            return Object.assign({}, result, {content})
        },
        answeredSummary(){
            const rows = []
            const form = this.form
            const push = (id, question, answer) => rows.push({id, question, answer})

            if (form.q1_shock) push('q1', this.content.questions[0].text, form.q1_shock === 'yes' ? this.content.labels.yes : this.content.labels.no)
            if (form.q1a_e_subtype) push('q1a', {"en-ca": "Subtype", "fr-ca": "Sous-type"}, this.content.questions[0].subOptions.find(o => o.value === form.q1a_e_subtype).label)
            if (form.q2_hypotension) push('q2', this.content.questions[1].text, form.q2_hypotension === 'yes' ? this.content.labels.yes : this.content.labels.no)
            if (form.q2a_d_subtype) push('q2a', {"en-ca": "Subtype", "fr-ca": "Sous-type"}, this.content.questions[1].subOptions.find(o => o.value === form.q2a_d_subtype).label)
            if (form.q3_symptoms) push('q3', this.content.questions[2].text, form.q3_symptoms === 'yes' ? this.content.labels.yes : this.content.labels.no)
            if (form.q4_subsegmental) push('q4', this.content.questions[3].text, this.content.questions[3].options.find(o => o.value === form.q4_subsegmental).label)
            if (form.q5_score_tool) {
                const opt = this.content.questions[4].options.find(o => o.value === form.q5_score_tool)
                push('q5', this.content.questions[4].text, {"en-ca": this.getLocale(opt.label) + ': ' + form.q5_score_value, "fr-ca": this.getLocale(opt.label) + ' : ' + form.q5_score_value})
            }
            if (form.q6_rv) push('q6', this.content.questions[5].text, this.content.questions[5].options.find(o => o.value === form.q6_rv).label)
            if (form.q7_biomarkers) push('q7', this.content.questions[6].text, this.content.questions[6].options.find(o => o.value === form.q7_biomarkers).label)
            if (form.q8_resp) {
                const group = getRespiratoryCriteriaGroup(form)
                const text = group ? this.content.respiratoryCriteriaText[group] : {"en-ca": "", "fr-ca": ""}
                push('q8', text, form.q8_resp === 'yes' ? this.content.labels.yes : this.content.labels.no)
            }
            return rows
        }
    },
    watch: {
        language(after){
            this.activeLanguage = after
        },
        activeLanguage(){
            let self = this
            setTimeout(() => {
                self.scrollTo('peClassification-header')
            }, 200)
        }
    }
}
</script>

<style lang="scss" scoped>
.debug{
    color: red;
    font-size: 0.7em;
}
.question{
    border: 1px solid lightgray;
    border-radius: 5px;
    margin: 15px;
    padding: 15px;
    font-size: 1.2em;
}
.question-subtitle{
    color: gray;
    font-size: 0.7em;
    font-style: italic;
    line-height: 1em;
    margin-top: 5px;
}

.question-input{
    min-height: 50px;
    width: 100%;
    padding: 5px;
    margin: 5px;
}

::v-deep .v-snack__wrapper {
    border: 3px solid whitesmoke !important;
}
.popup-wrapper {
    background-color: $secondary-blue-light-2;
}
.v-list-item__subtitle, .v-list-item__title {
    white-space: normal;
}

/* ---------------------------------------------------------------------------------------
 * PE Classification Tool — visual styling
 * Colour system loosely follows the AHA/ACC category groups referenced in the Brief
 * (A=green, B=blue, C=amber, D=orange, E=red) and mirrors the concept simulator, at
 * contrast ratios intended to meet WCAG 2.1 AA (verify against the final TC palette).
 * ------------------------------------------------------------------------------------- */
.pe-entry, .pe-question-flow, .pe-result-screen {
    padding: 10px 15px 25px;
}
.pe-lang-toggle {
    display: flex;
    gap: 6px;
    margin-bottom: 12px;
}
.pe-lang-pill {
    border: 1px solid #B0B6C8;
    background: #fff;
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 0.85em;
    cursor: pointer;
}
.pe-lang-pill.active {
    background: #1B2E6B;
    border-color: #1B2E6B;
    color: #fff;
}
.pe-guideline-badge {
    display: inline-block;
    font-size: 0.75em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #4A4F6A;
    background: #E8EBF2;
    border-radius: 20px;
    padding: 3px 10px;
    margin-bottom: 12px;
}
.pe-intro-text {
    font-size: 0.95em;
    line-height: 1.6;
    margin-bottom: 16px;
}
.pe-disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.9em;
    line-height: 1.4;
    margin-bottom: 16px;
    cursor: pointer;
}
.pe-disclaimer input {
    margin-top: 3px;
}
.pe-ack-text {
    font-size: 0.75em;
    color: #8A90A8;
    margin-top: 18px;
}

.pe-btn {
    font-family: inherit;
    font-size: 0.95em;
    border-radius: 6px;
    padding: 10px 18px;
    cursor: pointer;
    border: 1px solid #B0B6C8;
    background: #fff;
    color: #1A1A2E;
}
.pe-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.pe-btn-primary {
    background: #1B2E6B;
    border-color: #1B2E6B;
    color: #fff;
}
.pe-btn-secondary {
    background: #fff;
    border-color: #1B2E6B;
    color: #1B2E6B;
}
.pe-btn-launch {
    display: block;
    width: 100%;
    padding: 12px;
    font-weight: 600;
}
.pe-btn-back {
    background: transparent;
    border: none;
    color: #4A4F6A;
    padding: 8px 0;
}
.pe-progress-wrap {
    margin: 0 15px 5px;
}
.pe-progress-label {
    font-size: 0.75em;
    color: #8A90A8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 5px;
}
.pe-progress-track {
    height: 6px;
    border-radius: 3px;
    background: #E8EBF2;
    overflow: hidden;
}
.pe-progress-fill {
    height: 100%;
    background: #1B2E6B;
    transition: width 0.2s ease;
}

.pe-question-text {
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 10px;
}
.pe-help-toggle {
    background: none;
    border: none;
    color: #1B2E6B;
    font-size: 0.8em;
    cursor: pointer;
    padding: 0;
    margin-bottom: 8px;
}
.pe-help-body {
    background: #E6F1FB;
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 0.8em;
    line-height: 1.5;
    margin-bottom: 10px;
}
.pe-note {
    border: 1px solid orange;
    padding: 8px;
    font-size: 0.75em;
    color: #a07800;
    font-style: italic;
    margin-top: 10px;
    background-color: #fff5d8;
}

.pe-yesno-row {
    display: flex;
    gap: 10px;
    margin-top: 8px;
}
.pe-btn-yesno {
    flex: 1;
    padding: 14px;
    font-weight: 600;
    font-size: 1em;
}
.pe-btn-yes.selected {
    background: #EAF3DE;
    border-color: #3B6D11;
    color: #27500A;
}
.pe-btn-no.selected {
    background: #FCEBEB;
    border-color: #791F1F;
    color: #791F1F;
}

.pe-sub-options {
    margin-top: 14px;
    padding: 14px;
    border-top: 1px dashed #D0D4E0;
}
.pe-radio-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 5px;
    font-size: 0.9em;
    cursor: pointer;
}
.pe-radio-list {
    margin-top: 6px;
}
.pe-inline-input {
    margin-left: auto;
}
.pe-number-input {
    width: 110px;
    min-height: unset;
    border: 1px solid #B0B6C8;
    border-radius: 4px;
}
.pe-error {
    color: #791F1F;
    font-size: 0.8em;
    margin-top: 6px;
}

.pe-nav-row {
    margin: 5px 15px 0;
}
.pe-nav-row-split {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
}
.pe-btn-continue {
    margin-left: auto;
}

.pe-result-card {
    border-radius: 12px;
    padding: 20px;
    margin: 15px;
    border: 1px solid;
}
.pe-cat-a { background: #EAF3DE; border-color: #639922; color: #27500A; }
.pe-cat-b { background: #E6F1FB; border-color: #378ADD; color: #0C447C; }
.pe-cat-c { background: #FAEEDA; border-color: #EF9F27; color: #854F0B; }
.pe-cat-d { background: #FAECE7; border-color: #D85A30; color: #993C1D; }
.pe-cat-e { background: #FCEBEB; border-color: #E24B4A; color: #791F1F; }

.pe-result-top {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 8px;
}
.pe-result-badge {
    font-size: 2.2em;
    font-weight: 700;
    line-height: 1;
}
.pe-result-guideline {
    font-size: 0.7em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    opacity: 0.7;
}
.pe-result-name {
    font-weight: 600;
    margin-bottom: 6px;
}
.pe-result-desc {
    font-size: 0.9em;
    line-height: 1.6;
}
.pe-result-flag {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(0,0,0,0.15);
    font-size: 0.8em;
    font-style: italic;
}

.pe-inputs-summary {
    margin: 0 15px 15px;
    font-size: 0.85em;
}
.pe-inputs-summary summary {
    cursor: pointer;
    font-weight: 600;
    color: #1B2E6B;
}
.pe-inputs-summary ul {
    margin: 10px 0 0 20px;
    line-height: 1.6;
}

.pe-citation {
    font-size: 0.75em;
    color: #8A90A8;
    margin: 0 15px 15px;
}

.pe-result-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 0 15px;
}
.pe-copy-confirm {
    margin: 10px 15px 0;
    font-size: 0.8em;
    color: #27500A;
}

.pe-pdf-summary {
    padding: 20px;
}
.pe-pdf-classification {
    display: flex;
    align-items: baseline;
    gap: 10px;
    border-radius: 8px;
    border: 1px solid;
    padding: 10px 14px;
    margin: 8px 0;
}
.pe-pdf-classification-code {
    font-size: 1.6em;
    font-weight: 700;
}
.pe-pdf-classification-name {
    font-weight: 600;
}
.pe-snackbar {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #1A1A2E;
    color: #fff;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 0.85em;
    z-index: 100;
}
</style>

<style>
.calcItem #peClassification input[type="text"],
.calcItem #peClassification  input[type="number"]{
    all: unset;
}
.question .question-input sup{
    font-size: 0.6em;
}
.calcItem #peClassification .popup-wrapper a {
    color: blue;
    text-decoration: underline;
    cursor: pointer;
}
.standalone{
    display: block !important;
}

.pe-inline-input input[type="number"]{
    min-width: 200px !important;
    border: 1px solid #B0B6C8 !important;
    padding: 3px !important;
}
</style>